<template>
  <v-dialog v-model="show" max-width="350">
    <v-card v-if="car" class="rounded" :loading="loading" color="#406d59">
      <v-card-title class=" darken-4 pa-2 pa-md-3 justify-center" style="font-family: 'Oswald', sans-serif !important;font-weight: bold !important;">
        Reviews
      </v-card-title>
      <v-card-subtitle
        class="d-flex align-center darken-3 ma-0 pa-2 pa-md-3"
      >
        <v-avatar v-if="driver.picture" class="mr-1">
          <img :src="driver.picture" alt="Profile picture" />
        </v-avatar>
        <v-avatar v-else :color="car.routeColor" class="mr-1">
          <v-icon>mdi-account</v-icon>
        </v-avatar>
        <div class="mx-1">
          <p class="ma-0">{{ driver.name }}</p>
          <v-icon>mdi-bus</v-icon>
          <span class="font-weight-bold">{{ car.number }}</span>
        </div>
        <v-spacer></v-spacer>
        <span v-if="driver.rating" class="font-weight-bold">
          {{ driver.rating }}
        </span>
        <span v-else class="font-weight-bold">N/A</span>
        <v-icon>mdi-star</v-icon>
      </v-card-subtitle>
      <v-card-text class="darken-3 pa-2 pa-md-3">
        <v-textarea
          v-model="review"
          auto-grow
          filled
          hide-details
          no-resize
          placeholder="Write a review..."
          rounded
          rows="2"
          class="rounded"
        >
          <template slot="prepend-inner">
            <v-rating
              v-model="rating"
              clearable
              dense
              color="#bfc1ad"
              background-color="#bfc1ad"
            />
            <v-spacer></v-spacer>
            <v-btn icon :loading="loading" @click="$refs.img.click()">
              <input
                ref="img"
                name="img"
                accept="image/*"
                type="file"
                class="d-none"
                @change="selectImage"
              />
              <v-icon :color="color">{{ icon }}</v-icon>
            </v-btn>
          </template>
          <template slot="append">
            <v-btn
              fab
              small
              :disabled="!rating"
              :color="car.routeColor"
              :loading="loading"
              @click="submitReview"
            >
              <v-icon>mdi-send</v-icon>
            </v-btn>
          </template>
        </v-textarea>
      </v-card-text>
    </v-card>
    <v-snackbar v-model="error" bottom color="error" :timeout="10000">
      {{ errorMsg }}
      <v-btn dark text @click="error = false">
        Close
      </v-btn>
    </v-snackbar>
  </v-dialog>
</template>

<script>
import API from '../services/api'
import { storage } from '../services/firebase'
import { v1 as uuidv1 } from 'uuid'

export default {
  name: 'ReviewPopUp',
  props: {
    value: {
      type: Boolean,
      default: false
    },
    car: {
      type: Object,
      default: null
    },
    driver: {
      type: Object,
      default: null
    }
  },
  data: () => ({
    loading: false,
    error: false,
    errorMsg: null,
    rating: null,
    review: null,
    image: null,
    color: null,
    icon: 'mdi-camera'
  }),
  computed: {
    show: {
      get: function() {
        return this.value
      },
      set: function(value) {
        this.$emit('input', value)
      }
    }
  },
  methods: {
    selectImage: async function(event) {
      try {
        this.error = false
        this.loading = true
        let file = URL.createObjectURL(event.target.files[0])
        let img = await this.loadImage(file)
        this.image = await this.resizeImage(img)
        this.color = 'success'
        this.icon = 'mdi-check-bold'
      } catch (error) {
        this.color = 'error'
        this.icon = 'mdi-close-thick'
        this.errorMsg = error.message
        this.error = true
      } finally {
        this.loading = false
      }
    },
    loadImage: function(src) {
      return new Promise(resolve => {
        let img = new Image()
        img.src = src
        img.onload = () => resolve(img)
      })
    },
    resizeImage: async function(img) {
      let canvas = document.createElement('canvas')
      let { height, width } = img
      let MAX = 1000
      if (height > width) {
        if (height > MAX) {
          width *= MAX / height
          height = MAX
        }
      } else {
        if (width > MAX) {
          height *= MAX / width
          width = MAX
        }
      }
      canvas.height = height
      canvas.width = width
      let ctx = canvas.getContext('2d')
      ctx.drawImage(img, 0, 0, width, height)
      let blob = await this.toBlob(ctx.canvas)
      let fileName = uuidv1() + '.jpg'
      let file = new File([blob], fileName, { type: 'image/jpeg' })
      return file
    },
    toBlob: function(canvas) {
      return new Promise(resolve => {
        canvas.toBlob(blob => resolve(blob), 'image/jpeg', 0.8)
      })
    },
    submitReview: async function() {
      try {
        this.error = false
        this.loading = true
        let review = {
          car: this.car.id,
          driver: this.driver.id,
          rate: this.rating
        }
        if (this.review) {
          review.review = this.review
        }
        if (this.image) {
          review.image = await this.uploadImage(this.image)
        }
        await API.postReview(review)
        this.$emit('close')
      } catch (error) {
        this.errorMsg = error.message
        this.error = true
      } finally {
        this.loading = false
      }
    },
    uploadImage: async function(file) {
      let storageRef = storage().ref()
      let imgRef = storageRef.child(`img_reviews/${file.name}`)
      let result = await imgRef.put(file)
      return result.metadata.name
    }
  }
}
</script>

<style>
.rounded {
  border-radius: 12px !important;
}
.rounded .v-input__slot {
  padding: 0 12px !important;
  flex-direction: column;
}
.rounded .v-input__slot .v-input__prepend-inner {
  display: flex;
  align-items: center;
  width: 100%;
  margin: 0 !important;
  padding: 12px 0 0 0 !important;
}
.rounded .v-input__slot .v-input__append-inner {
  display: flex;
  justify-content: flex-end;
  width: 100%;
  margin: 0 !important;
  padding: 0 0 12px 0 !important;
}
</style>
